import AnalyticsFilterInputs from "../../types/analytics/AnalyticsFilterInputs";
import { AudienceFacetType } from "./audienceFacetHelpers";

/**
 * Staged vs applied comparisons for the analytics filter band.
 * Overall dirty includes panel-only fields (active users, comparison, etc.) that
 * have no chip; per-facet helpers drive which chips get the amber halo.
 */
export function analyticsFiltersAreDirty(
  selected: AnalyticsFilterInputs,
  applied: AnalyticsFilterInputs
): boolean {
  return (
    isJourneyFilterDirty(selected, applied) ||
    isDateFilterDirty(selected, applied) ||
    isAudienceFacetDirty("LOCATION", selected, applied) ||
    isAudienceFacetDirty("JOB-TITLE", selected, applied) ||
    isAudienceFacetDirty("APPRAISAL-LEVEL", selected, applied) ||
    isAudienceFacetDirty("USER-GROUP", selected, applied) ||
    isPointInTimeFilterDirty(selected, applied) ||
    selected.activeUsersOnly !== applied.activeUsersOnly ||
    selected.includePartiallyApproved !== applied.includePartiallyApproved ||
    selected.comparisonProperty !== applied.comparisonProperty
  );
}

/**
 * Show past data changes which version of reality the charts read, so a change
 * is staged like any other filter and needs Apply (ADR-0006).
 */
export function isPointInTimeFilterDirty(
  selected: AnalyticsFilterInputs,
  applied: AnalyticsFilterInputs
): boolean {
  return (
    normalisedPointInTime(selected) !== normalisedPointInTime(applied)
  );
}

/** Null, undefined, and blank all mean "live", so they must compare equal. */
function normalisedPointInTime(values: AnalyticsFilterInputs): string | null {
  return values.pointInTimeDate?.trim() || null;
}

export function isJourneyFilterDirty(
  selected: AnalyticsFilterInputs,
  applied: AnalyticsFilterInputs
): boolean {
  return (
    selected.allJourneys !== applied.allJourneys ||
    !sameIdList(selected.journeyKeys, applied.journeyKeys)
  );
}

export function isDateFilterDirty(
  selected: AnalyticsFilterInputs,
  applied: AnalyticsFilterInputs
): boolean {
  return !sameDateRange(selected.dateRange, applied.dateRange);
}

export function isAudienceFacetDirty(
  type: AudienceFacetType,
  selected: AnalyticsFilterInputs,
  applied: AnalyticsFilterInputs
): boolean {
  switch (type) {
    case "LOCATION":
      return !sameIdList(selected.locationIds, applied.locationIds);
    case "JOB-TITLE":
      return !sameIdList(selected.jobTitleIds, applied.jobTitleIds);
    case "APPRAISAL-LEVEL":
      return !sameIdList(selected.appraisalLevelIds, applied.appraisalLevelIds);
    case "USER-GROUP":
      return !sameIdList(selected.userGroupIds, applied.userGroupIds);
  }
}

function sameIdList(
  a: Array<string | number> | null | undefined,
  b: Array<string | number> | null | undefined
): boolean {
  const aa = a ?? [];
  const bb = b ?? [];
  if (aa.length !== bb.length) return false;
  const sortedA = [...aa].map(String).sort();
  const sortedB = [...bb].map(String).sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

/** True when both ranges are null/undefined or share the same start/end instants. */
export function sameDateRange(
  a: AnalyticsFilterInputs["dateRange"],
  b: AnalyticsFilterInputs["dateRange"]
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const aStart = a.startDate ? new Date(a.startDate).getTime() : 0;
  const aEnd = a.endDate ? new Date(a.endDate).getTime() : 0;
  const bStart = b.startDate ? new Date(b.startDate).getTime() : 0;
  const bEnd = b.endDate ? new Date(b.endDate).getTime() : 0;
  return aStart === bStart && aEnd === bEnd;
}

export function formatAudienceChipLabel(values: AnalyticsFilterInputs): string {
  const parts: string[] = [];
  const levelCount = values.appraisalLevelIds?.length ?? 0;
  const locationCount = values.locationIds?.length ?? 0;
  const titleCount = values.jobTitleIds?.length ?? 0;
  const groupCount = values.userGroupIds?.length ?? 0;

  if (levelCount) parts.push(levelCount === 1 ? "1 job level" : `${levelCount} job levels`);
  if (locationCount) parts.push(locationCount === 1 ? "1 location" : `${locationCount} locations`);
  if (titleCount) parts.push(titleCount === 1 ? "1 job title" : `${titleCount} job titles`);
  if (groupCount) parts.push(groupCount === 1 ? "1 user group" : `${groupCount} user groups`);

  if (parts.length === 0) return "Everyone";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} · ${parts[1]}`;
  return `${parts[0]} · ${parts[1]} +${parts.length - 2}`;
}
